import { IndexSort } from "@omni/types/api/search/algolia/shared/enum/indexSort.enum";
import { isEnum } from "@omni/utils/enum.utils";
import { isDefined } from "@omni/utils/helper.utils";
import { parseIntSafe } from "@omni/utils/number.utils";
import queryString from "query-string";

import { getOperator } from "@/components/Search/SearchResults/components/results/components/productsResults/components/facets/components/facet/facet.configuration";
import { DefaultHitsPerPage } from "@/components/Search/SearchResults/components/results/components/productsResults/components/resultsView/components/filters/components/productHitsPerPage/hitsPerPage.config";
import { SearchConfiguration } from "@/redux/modules/value/search/search.types";
import {
    toHierarchicalFacetName,
    toHierarchicalFacetPath
} from "@/services/bffSearch/responseTransformers/mergeHierarchicalFacets.transformer";
import { getDefaultConfiguration } from "@/utils/search/defaultConfiguration";
import {
    FACET_FILTER_SEPARATOR,
    NUMERIC_FILTER_REGEX,
    NUMERIC_FILTER_SEPARATOR
} from "@/utils/search/routing/routing.configuration";
import {
    GroupToPrefixMap,
    Route,
    TypeToRoutePropertyMap
} from "@/utils/search/routing/types/route.types";
import {
    FacetFilter,
    FacetsConfiguration,
    NumericFilter
} from "@/utils/search/types/searchConfiguration";

import { SearchComponent, SearchComponentEnum } from "../types/searchComponent";

const extractNumericFiltersFromRoute = ({
    facetName,
    facetValue,
    facetsConfiguration
}: ExtractFacetsFromRouteParams) => {
    const [lowerBound, upperBound] = facetValue
        .toString()
        .split(NUMERIC_FILTER_SEPARATOR)
        .map(parseFloat)
        .map((bound) => (Number.isNaN(bound) ? undefined : bound));

    if (!isDefined(lowerBound) && !isDefined(upperBound))
        return facetsConfiguration;

    const numericFilter: NumericFilter = {
        name: facetName,
        ...(isDefined(lowerBound) && {
            lowerBound: lowerBound
        }),
        ...(isDefined(upperBound) && {
            upperBound: upperBound
        })
    };

    facetsConfiguration.numericFilters = [
        ...facetsConfiguration.numericFilters,
        numericFilter
    ];

    return facetsConfiguration;
};

const extractHierarchicalFilterFromRoute = ({
    facetName,
    facetValue,
    facetsConfiguration
}: ExtractFacetsFromRouteParams) => {
    const level = toHierarchicalFacetPath(facetValue.toString()).length - 1;
    const hierarchicalFacetName = toHierarchicalFacetName(facetName, level);

    const facetFilter: FacetFilter = {
        name: hierarchicalFacetName,
        operator: getOperator(facetName),
        selectedValues: [facetValue.toString()]
    };

    facetsConfiguration.facetFilters = [
        ...facetsConfiguration.facetFilters,
        facetFilter
    ];

    return facetsConfiguration;
};

const extractFacetFiltersFromRoute = ({
    facetName,
    facetValue,
    facetsConfiguration
}: ExtractFacetsFromRouteParams) => {
    const selectedValues = facetValue.toString().split(FACET_FILTER_SEPARATOR);

    const facetFilter: FacetFilter = {
        name: facetName,
        operator: getOperator(facetName),
        selectedValues: selectedValues
    };

    facetsConfiguration.facetFilters = [
        ...facetsConfiguration.facetFilters,
        facetFilter
    ];

    return facetsConfiguration;
};

const facetsFromRouteParamsExtractors: Array<{
    facetValuesRegex?: RegExp;
    facetNames?: Array<string>;
    extractor: (params: ExtractFacetsFromRouteParams) => FacetsConfiguration;
}> = [
    {
        facetValuesRegex: NUMERIC_FILTER_REGEX,
        extractor: extractNumericFiltersFromRoute
    },
    {
        facetNames: ["hierarchicalCategories"],
        extractor: extractHierarchicalFilterFromRoute
    },
    {
        extractor: extractFacetFiltersFromRoute
    }
];

const extractFacetsFromRoute = (
    extractFacetsFromRouteParams: ExtractFacetsFromRouteParams
) =>
    facetsFromRouteParamsExtractors
        .find((facetsFromRouteParamsExtractor) => {
            if (isDefined(facetsFromRouteParamsExtractor.facetValuesRegex)) {
                return facetsFromRouteParamsExtractor.facetValuesRegex.test(
                    extractFacetsFromRouteParams.facetValue.toString()
                );
            }

            if (isDefined(facetsFromRouteParamsExtractor.facetNames)) {
                return facetsFromRouteParamsExtractor.facetNames.includes(
                    extractFacetsFromRouteParams.facetName
                );
            }

            return true;
        })
        ?.extractor(extractFacetsFromRouteParams);

export const parseUrl = () => {
    const routeState = queryString.parse(
        queryString.exclude(location.search.replaceAll("+", "%20"), [
            "wcmmode"
        ]),
        { parseNumbers: false }
    );

    return {
        ...routeState,
        ...(routeState.q !== undefined && {
            q: routeState.q?.toString()
        })
    } as Route;
};

type ExtractFacetsFromRouteParams = {
    facetName: string;
    facetValue: string | number;
    facetsConfiguration: FacetsConfiguration;
};

const routeToConfiguration = (
    { q, ...routeProperties }: Route,
    ignoreQuery: boolean
): Partial<SearchConfiguration> => {
    const defaultConfigurationMap = getDefaultConfiguration();

    const {
        [`${GroupToPrefixMap.PRODUCT}.${TypeToRoutePropertyMap.HITS_PER_PAGE}`]:
            productHitsPerPageString,
        [`${GroupToPrefixMap.PRODUCT}.${TypeToRoutePropertyMap.PAGINATION}`]:
            productPage,
        [`${GroupToPrefixMap.PRODUCT}.${TypeToRoutePropertyMap.SORT}`]:
            productSort,
        [`${GroupToPrefixMap.CONTENT}.${TypeToRoutePropertyMap.HITS_PER_PAGE}`]:
            contentHitsPerPageString,
        [`${GroupToPrefixMap.CONTENT}.${TypeToRoutePropertyMap.PAGINATION}`]:
            contentPage,
        [`${GroupToPrefixMap.CONTENT}.${TypeToRoutePropertyMap.SORT}`]:
            _contentSort,
        ...facetRouteProperties
    } = routeProperties ?? {};

    const productHitsPerPage = parseIntSafe(
        productHitsPerPageString,
        DefaultHitsPerPage
    );
    const contentHitsPerPage = parseIntSafe(
        contentHitsPerPageString,
        DefaultHitsPerPage
    );

    const facetsConfiguration = Object.entries(facetRouteProperties)
        .filter(
            ([facetKey, _]) =>
                facetKey.startsWith(`${GroupToPrefixMap.PRODUCT}.`) ||
                facetKey.startsWith(`${GroupToPrefixMap.CONTENT}.`)
        )
        .reduce(
            (facetsConfiguration, [facetKey, facetValue]) =>
                extractFacetsFromRoute({
                    facetName: facetKey.substring(2),
                    facetValue: facetValue,
                    facetsConfiguration:
                        facetsConfiguration as FacetsConfiguration
                }) ?? facetsConfiguration,
            defaultConfigurationMap[SearchComponent.PRODUCT_FACETS]
        );

    const componentConfigurations = [
        {
            ...defaultConfigurationMap[SearchComponent.PRODUCT_HITS_PER_PAGE],
            hitsPerPage: productHitsPerPage
        },
        {
            ...defaultConfigurationMap[SearchComponent.PRODUCT_PAGINATION],
            hitsRequested:
                productHitsPerPage * (parseIntSafe(productPage, 0) + 1)
        },
        {
            ...defaultConfigurationMap[SearchComponent.PRODUCT_SORT],
            ...(isEnum(IndexSort, productSort) && { sortBy: productSort })
        },
        {
            ...defaultConfigurationMap[SearchComponent.CONTENT_HITS_PER_PAGE],
            hitsPerPage: contentHitsPerPage
        },
        {
            ...defaultConfigurationMap[SearchComponent.CONTENT_PAGINATION],
            hitsRequested:
                contentHitsPerPage * (parseIntSafe(contentPage, 0) + 1)
        },
        {
            ...defaultConfigurationMap[SearchComponent.PRODUCT_FACETS],
            ...facetsConfiguration
        }
    ];

    const overriddenComponents: Array<SearchComponentEnum> =
        componentConfigurations.map(({ component }) => component);

    const defaultConfigurations = Object.values(defaultConfigurationMap).filter(
        (defaultConfiguration) =>
            !overriddenComponents.includes(defaultConfiguration.component)
    );

    return {
        ...(q && !ignoreQuery ? { query: q } : {}),
        components: [...componentConfigurations, ...defaultConfigurations]
    };
};

const configurationFromUrl = (ignoreQuery: boolean) => {
    const route = parseUrl();
    return routeToConfiguration(route, ignoreQuery);
};

export { configurationFromUrl };
